import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  makeStyles,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { RVModelOrderClient } from "../../../../api/api";
import { UserGroupChild } from "../../../../api/usergroup";
import { ValidationErrorReasons } from "../../../../graphql/errors";

const useStyles = makeStyles({
  select: {
    minWidth: 300,
  },
});

export interface AssignGroupModalClosed {
  cancelled: boolean;
  // why the order could not be assigned
  err?: string;
}

export interface AssignGroupModalProps {
  open: boolean;
  modelID?: string;
  orderID?: string;
  // the user group the order is currently assigned to
  userGroupID?: string;
  // the groups the order can be assigned to: the model's group and its child groups
  groups: UserGroupChild[];
  onClose: (e: AssignGroupModalClosed) => void;
  assign?: (
    modelID: string,
    orderID: string,
    userGroupID: string
  ) => Promise<unknown>;
}

// Assigns an order to the group of its model or one of that group's child
// groups. Members of a child group see only the orders assigned to it.
export const AssignGroupModal = ({
  open,
  modelID,
  orderID,
  userGroupID,
  groups,
  onClose,
  assign = (m, o, g): Promise<unknown> =>
    RVModelOrderClient.setUserGroup(m, o, g),
}: AssignGroupModalProps): JSX.Element => {
  const classes = useStyles();
  const [selected, setSelected] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // start from the group the order is in, if it is one that can be chosen
  useEffect(() => {
    if (open) {
      const current = groups.find((g) => g.id === userGroupID);
      setSelected(current ? current.id : "");
    }
  }, [open, userGroupID, groups]);

  const handleCancel = (): void => onClose({ cancelled: true });
  const handleAssign = async (): Promise<void> => {
    if (!modelID || !orderID || "" === selected) return;
    setSubmitting(true);
    try {
      await assign(modelID, orderID, selected);
      onClose({ cancelled: false });
    } catch (e) {
      console.error(e);
      onClose({
        cancelled: false,
        err:
          ValidationErrorReasons(e).join(". ") ||
          "Failed to assign the order to the group",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleCancel} data-testid="assign-group">
      <DialogTitle>Assign order to group</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Members of a child group see only the orders assigned to it. Members
          of the model&apos;s group see the orders of every child group.
        </DialogContentText>
        <FormControl className={classes.select}>
          <InputLabel id="assign-group-label">Group</InputLabel>
          <Select
            labelId="assign-group-label"
            value={selected}
            disabled={submitting}
            onChange={(e): void => setSelected(e.target.value as string)}
            inputProps={{ "data-testid": "assign-group-select" }}
          >
            {groups.map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          color="primary"
          data-testid="assign-group-submit"
          disabled={submitting || "" === selected || selected === userGroupID}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
};
